import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Spinner from '../components/Spinner';
import { getPublicAttendanceVerification } from '../lib/api';
import { formatDate, formatDateTime, formatTime } from '../lib/utils';
import type { PublicAttendanceVerification } from '../types';

const VERIFICATION_STATEMENT = 'Rekod kehadiran ini dijana secara elektronik melalui aplikasi Kehadiran. Bagi kehadiran melalui pengesahan pengguna, identiti disahkan menggunakan e-mel berdaftar (@mtib.gov.my) dan kod pengesahan sekali guna (OTP). Kaedah perekodan kehadiran dinyatakan pada rekod berkenaan. Tandatangan manual adalah tidak diperlukan.';

function checkInMethodLabel(method: PublicAttendanceVerification['roster'][number]['checkin_method']): string {
  return method === 'Legacy' ? 'Rekod lama' : method;
}

function verificationStatusLabel(status: PublicAttendanceVerification['verification_status']): string {
  return status === 'SAH' ? 'Sah' : status;
}

export default function AttendanceVerificationPage() {
  const { token = '' } = useParams();
  const [verification, setVerification] = useState<PublicAttendanceVerification | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setInvalid(false);

    void getPublicAttendanceVerification(token)
      .then((result) => {
        if (!active) return;
        setVerification(result);
        setInvalid(!result);
      })
      .catch(() => {
        if (!active) return;
        setVerification(null);
        setInvalid(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [token]);

  return <main className="verification-page">
    <section className="verification-panel" aria-labelledby="verification-title">
      <header className="verification-brand">
        <img src="/mtib-logo.png" alt="" aria-hidden="true" />
        <span><strong>MTIB</strong><small>Kehadiran</small></span>
      </header>

      {loading ? <div className="verification-loading"><Spinner label="Mengesahkan rekod kehadiran…" /></div> : null}

      {!loading && invalid ? <div className="verification-invalid" role="status">
        <h1 id="verification-title">Pengesahan Rekod Kehadiran</h1>
        <p>Rekod pengesahan tidak ditemui atau tidak sah.</p>
      </div> : null}

      {!loading && verification ? <>
        <div className="verification-heading">
          <h1 id="verification-title">Pengesahan Rekod Kehadiran</h1>
          <span className="verification-status">Sah</span>
        </div>

        <section className="verification-section" aria-labelledby="meeting-information-title">
          <h2 id="meeting-information-title">Maklumat Mesyuarat</h2>
          <dl className="verification-metadata">
            <div><dt>Tajuk Mesyuarat</dt><dd>{verification.title}</dd></div>
            <div><dt>Tarikh</dt><dd>{formatDate(verification.meeting_date)}</dd></div>
            <div><dt>Waktu</dt><dd>{formatTime(verification.meeting_time)}</dd></div>
            <div><dt>Lokasi</dt><dd>{verification.location || 'Tidak dinyatakan'}</dd></div>
            {verification.chairman ? <div><dt>Pengerusi</dt><dd>{verification.chairman}</dd></div> : null}
          </dl>
        </section>

        <section className="verification-section" aria-labelledby="verification-information-title">
          <h2 id="verification-information-title">Maklumat Pengesahan</h2>
          <dl className="verification-metadata">
            <div><dt>Status Pengesahan</dt><dd>{verificationStatusLabel(verification.verification_status)}</dd></div>
            <div><dt>Jumlah Kehadiran</dt><dd>{verification.valid_attendance_count}</dd></div>
            <div><dt>Kod Rujukan</dt><dd>{verification.reference_code}</dd></div>
            <div><dt>Tarikh/Masa Dijana</dt><dd>{formatDateTime(verification.generated_at)}</dd></div>
            <div><dt>Dijana oleh</dt><dd>{verification.generated_by_name}</dd></div>
            <div><dt>Bahagian</dt><dd>{verification.generated_by_department}</dd></div>
            <div><dt>Sumber</dt><dd>aplikasi Kehadiran</dd></div>
          </dl>
        </section>

        <section className="verification-section verification-roster" aria-labelledby="verification-roster-title">
          <h2 id="verification-roster-title">Senarai Kehadiran</h2>
          <table className="verification-roster-table">
            <thead><tr><th>No.</th><th>Nama</th><th>Bahagian / Organisasi</th><th>Kaedah Check-in</th></tr></thead>
            <tbody>{verification.roster.length ? verification.roster.map((row, index) => <tr key={`${row.name}-${row.department_org}-${index}`}>
              <td data-label="No.">{index + 1}</td>
              <td data-label="Nama">{row.name}</td>
              <td data-label="Bahagian / Organisasi">{row.department_org}</td>
              <td data-label="Kaedah Check-in">{checkInMethodLabel(row.checkin_method)}</td>
            </tr>) : <tr className="verification-roster-empty"><td colSpan={4}>Tiada kehadiran yang disahkan.</td></tr>}</tbody>
          </table>
        </section>

        <p className="verification-statement">{VERIFICATION_STATEMENT}</p>
      </> : null}
    </section>
  </main>;
}
